/* jshint esnext: true */
import { Controller } from '@hotwired/stimulus';
import { Modal } from 'bootstrap';

export default class extends Controller {
  static targets = ['memo', 'saveButton', 'memoTextarea'];
  static values = { isAdmin: Boolean };

  connect() {
    this.setupEditMemoButtons();

    if (this.isAdminValue) {
      this.addMonoClassToSalaryCells();
    }
  }

  // Handle modal cleanup when hidden
  modalHidden(event) {
    const resultMessage = event.currentTarget.querySelector('[data-result-message]');
    if (resultMessage) {
      const firstDiv = resultMessage.querySelector('div:first-child');
      if (firstDiv) {
        firstDiv.remove();
      }
    }
  }

  // Track dirty state on textarea input
  markDirty(event) {
    const textarea = event.currentTarget;
    const initial = textarea.dataset.initialValue || '';
    const isDirty = textarea.value !== initial;
    const modal = textarea.closest('.modal');
    this._setModalDirty(modal, isDirty);
  }

  // Confirm before navigating to prev/next employee modal when dirty
  confirmLeaveIfDirty(event) {
    const button = event.currentTarget;
    const modalId = button.dataset.modalId;
    const nextModalId = button.dataset.nextId;
    const modal = document.querySelector(`#upinfo${modalId}`);

    if (this._isModalDirty(modal)) {
      const ok = window.confirm(
        '保存していない変更があります。\nこのまま別の従業員に切り替えると、入力内容は破棄されます。\n\nOK: 変更を破棄して切り替える\nキャンセル: このまま編集を続ける'
      );
      if (!ok) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      this._discardChanges(modal);
    }

    const currentModal = document.getElementById(`upinfo${modalId}`);
    const nextModal = document.getElementById(`upinfo${nextModalId}`);
    if (currentModal) { Modal.getOrCreateInstance(currentModal).hide(); }
    if (nextModal) { Modal.getOrCreateInstance(nextModal).show(); }
  }

  // Confirm before closing the modal (close button, ESC, backdrop click) when dirty
  confirmHideIfDirty(event) {
    const modal = event.currentTarget;
    if (!this._isModalDirty(modal)) return;

    const ok = window.confirm(
      '保存していない変更があります。\nこのままモーダルを閉じると、入力内容は破棄されます。\n\nOK: 変更を破棄して閉じる\nキャンセル: このまま編集を続ける'
    );
    if (!ok) {
      event.preventDefault();
      return;
    }
    this._discardChanges(modal);
  }

  // メモ更新フォームの送信 (data-turbo=false のまま fetch で送る)。
  // memos#update は head :ok / :unprocessable_entity を返すので status で成否判定する。
  async submitMemo(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const resultMessage = document.getElementById(`upinfo_${form.dataset.id}_result_message`);

    let ok = false;
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      ok = response.ok;
    } catch {
      ok = false;
    }

    if (ok) {
      if (resultMessage && !resultMessage.querySelector('.memo-alert-success')) {
        resultMessage.insertAdjacentHTML('beforeend',
          "<div class='alert alert-success memo-alert-success'>メモを更新しました</div>");
      }
      // form > .modal の構造のため closest ではなく querySelector で modal を取る
      const modal = form.querySelector('.modal');
      if (modal) {
        const textarea = modal.querySelector('#puMemo');
        if (textarea) {
          textarea.dataset.initialValue = textarea.value;
        }
        this._setModalDirty(modal, false);
      }
    } else if (resultMessage && !resultMessage.querySelector('.memo-alert-danger')) {
      resultMessage.insertAdjacentHTML('beforeend',
        "<div class='alert alert-danger memo-alert-danger'>メモを更新できませんでした</div>");
    }
  }

  // 自動計算(管理者用)。calc は JSON を返すが結果を消費する画面要素は無いため、
  // リクエストのみ発火する (旧 remote:true の挙動を維持)。
  adminCalc(event) {
    event.preventDefault();
    fetch(event.currentTarget.href, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).catch(() => {});
  }

  // Setup edit memo button handlers
  setupEditMemoButtons() {
    document.querySelectorAll('.btn-edit-memo').forEach((el) => {
      el.addEventListener('click', (ev) => {
        const modal = el.closest('.modal');
        modal.querySelector('#puMemo').removeAttribute('disabled');
        modal.querySelector('.save-memo-button').classList.remove('d-none');
      });
    });
  }

  // Add monospace class to salary cells for admin users
  addMonoClassToSalaryCells() {
    document.querySelectorAll('td.salary').forEach(el => {
      el.classList.add('mono');
    });
    document.querySelectorAll('td.itemTotal').forEach(el => {
      el.classList.add('mono');
    });
  }

  // Validate memo text length (max 3 lines, 40 chars per line)
  checkMemoLength(event) {
    const charCode = event.charCode;
    const separateLines = event.target.value.split(/\r?\n|\r|\n/g);

    if (separateLines.length > 3) {
      // Allow backspace (8), delete (46), and enter (13)
      if (!(charCode == 46 || charCode == 8 || charCode == 13)) {
        event.preventDefault();
      }
    }

    separateLines.forEach((line) => {
      if (line.length > 40) {
        if (!(charCode == 46 || charCode == 8 || charCode == 13)) {
          event.preventDefault();
          return false;
        }
      }
    });
  }

  // dirty=true: enable + 青(btn-primary) / dirty=false: disabled + グレー(btn-secondary)
  _setModalDirty(modal, isDirty) {
    if (!modal) return;
    const saveButton = modal.querySelector('.save-memo-button');
    if (saveButton) {
      if (isDirty) {
        saveButton.disabled = false;
        saveButton.classList.remove('btn-secondary', 'disabled');
        saveButton.classList.add('btn-primary');
      } else {
        saveButton.disabled = true;
        saveButton.classList.remove('btn-primary');
        saveButton.classList.add('btn-secondary', 'disabled');
      }
    }
    modal.dataset.dirty = isDirty ? 'true' : 'false';
  }

  _isModalDirty(modal) {
    return modal && modal.dataset.dirty === 'true';
  }

  _discardChanges(modal) {
    if (!modal) return;
    const textarea = modal.querySelector('#puMemo');
    if (textarea) {
      textarea.value = textarea.dataset.initialValue || '';
    }
    this._setModalDirty(modal, false);
  }
}
